import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authAPI, welcomeAPI, paymentAPI, chatAPI } from '../services/api'; // api.js dosyasını import ediyoruz
import { fetchRecoverableCountrySlug } from '../utils/countryRecovery';
import { logPhaseTimings } from '../utils/perfLog';

const AppContext = createContext();

const initialState = {
  currentUser: null,
  sessionId: null,
  isLoading: true, // Uygulama başlarken yükleme durumunu true yapıyoruz
  uploadedFiles: [],
  chatHistory: [],
  hasStartedChat: false,
  userMessageCount: 0,
  visaTips: [],
  hasCompletedPayment: false,
  conversationId: null,
  selectedVisaApplication: null, // Track selected visa application
  selectedCountry: null, // Track selected country for payment
  welcomeSeen: null, // Has the user completed the /welcome flow? null = not yet known
  notificationPackage: null // Appointment-notification package window for the countdown; null = not yet fetched
};

function appReducer(state, action) {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      // Store user record; sessionId persistence (localStorage vs sessionStorage)
      // is handled by authAPI.login() via sessionStore so the "Beni Hatırla"
      // choice is respected.
      localStorage.setItem('currentUser', JSON.stringify(action.payload.user));
      return {
        ...state,
        currentUser: action.payload.user,
        sessionId: action.payload.sessionId,
        isLoading: false,
      };
    case 'LOGOUT':
      // Clear all user data from localStorage + sessionStorage
      localStorage.removeItem('currentUser');
      localStorage.removeItem('sessionId');
      sessionStorage.removeItem('sessionId');
      // Clear payment status on logout
      if (state.currentUser) {
        const paymentKey = `italyVisaPaymentCompleted_${state.currentUser.UserID}`;
        localStorage.removeItem(paymentKey);

        // Clear selected visa application on logout
        const visaKey = `selectedVisaApplication_${state.currentUser.UserID}`;
        localStorage.removeItem(visaKey);

        // Clear selected country on logout
        const countryKey = `selectedCountry_${state.currentUser.UserID}`;
        localStorage.removeItem(countryKey);

        // Reset post-payment redirect so next login triggers it again
        sessionStorage.removeItem(`postPaymentRedirectDone_${state.currentUser.UserID}`);
      }
      return {
        ...initialState,
        isLoading: false,
      };
    case 'SET_USER':
      return {
        ...state,
        currentUser: action.payload,
      };
    case 'UPDATE_USER':
      const updatedUser = action.payload;
      // Update localStorage as well
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
      return {
        ...state,
        currentUser: updatedUser,
      };
    case 'SET_SESSION':
      return {
        ...state,
        sessionId: action.payload,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    case 'ADD_UPLOADED_FILE':
      return {
        ...state,
        uploadedFiles: [...state.uploadedFiles, action.payload],
      };
    case 'REMOVE_UPLOADED_FILE':
      return {
        ...state,
        uploadedFiles: state.uploadedFiles.filter((_, index) => index !== action.payload),
      };
    case 'SET_UPLOADED_FILES':
      return {
        ...state,
        uploadedFiles: action.payload,
      };
    case 'SET_CHAT_HISTORY':
      // Replace the whole history (used to restore persisted per-country history on chat open).
      return {
        ...state,
        chatHistory: action.payload,
        hasStartedChat: action.payload.length > 0 ? true : state.hasStartedChat,
      };
    case 'ADD_CHAT_MESSAGE':
      return {
        ...state,
        chatHistory: [...state.chatHistory, action.payload],
      };
    case 'UPDATE_CHAT_MESSAGE':
      // Update an existing message (for streaming)
      return {
        ...state,
        chatHistory: state.chatHistory.map(msg =>
          msg.id === action.payload.id
            ? { ...msg, ...action.payload }
            : msg
        ),
      };
    case 'REMOVE_LOADING_MESSAGE':
      return {
        ...state,
        chatHistory: state.chatHistory.filter(msg => !msg.isLoading),
      };
    case 'REMOVE_LAST_MESSAGE':
      return {
        ...state,
        chatHistory: state.chatHistory.slice(0, -1),
      };
    case 'CLEAR_CHAT_HISTORY':
      return {
        ...state,
        chatHistory: [],
        hasStartedChat: false,
        userMessageCount: 0,
      };
    case 'SET_VISA_TIPS':
      return {
        ...state,
        visaTips: action.payload,
      };
    case 'SET_PAYMENT_COMPLETED':
      const newState = {
        ...state,
        hasCompletedPayment: action.payload,
      };
      if (state.currentUser) {
        const key = `italyVisaPaymentCompleted_${state.currentUser.UserID}`;
        if (action.payload) {
          localStorage.setItem(key, 'true');
          // Clear free chat limit lock for this country since user has paid
          if (state.selectedCountry) {
            localStorage.removeItem(`freeChatLimit_${state.currentUser.UserID}_${state.selectedCountry}`);
          }
        } else {
          localStorage.removeItem(key);
        }
      }
      return newState;
    case 'SET_NOTIFICATION_PACKAGE':
      // Not persisted to localStorage — always recomputed from a fresh fetch so
      // the countdown never goes stale.
      return {
        ...state,
        notificationPackage: action.payload,
      };
    case 'SET_CONVERSATION_ID':
      return {
        ...state,
        conversationId: action.payload,
      };
    case 'SET_SELECTED_VISA_APPLICATION':
      const newStateWithVisa = {
        ...state,
        selectedVisaApplication: action.payload,
      };
      // Store in localStorage with user-specific key
      if (state.currentUser) {
        const key = `selectedVisaApplication_${state.currentUser.UserID}`;
        if (action.payload) {
          localStorage.setItem(key, JSON.stringify(action.payload));
        } else {
          localStorage.removeItem(key);
        }
      }
      return newStateWithVisa;
    case 'SET_WELCOME_STATUS':
      return {
        ...state,
        welcomeSeen: action.payload,
      };
    case 'SET_SELECTED_COUNTRY':
      const newStateWithCountry = {
        ...state,
        selectedCountry: action.payload,
      };
      // Store in localStorage with user-specific key
      if (state.currentUser) {
        const key = `selectedCountry_${state.currentUser.UserID}`;
        if (action.payload) {
          localStorage.setItem(key, action.payload);
        } else {
          localStorage.removeItem(key);
        }
      }
      return newStateWithCountry;
    default:
      return state;
  }
}

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  useEffect(() => {
    const verifyUserSession = async () => {
      const sessionId = sessionStorage.getItem('sessionId') || localStorage.getItem('sessionId');

      if (sessionId) {
        // T10 instrumentation: measure each phase of the session-restore waterfall so the
        // dominant cost is measurable instead of guessed. No-op in production — see
        // logPhaseTimings.
        const perfStart = performance.now();
        try {
          const verifyStart = performance.now();
          const data = await authAPI.verifySession();
          const verifyMs = performance.now() - verifyStart;

          if (data.success) {
            // T10: fire welcome-status and notification-package CONCURRENTLY — both only
            // depend on the now-verified session, not on each other's result. Still await
            // welcome status specifically before LOGIN_SUCCESS: that ordering is deliberate
            // (the first render, and any "/" landing redirect, already needs to know where to
            // send the user). The notification-package promise is left running in the
            // background — it's not needed for first render — and is applied only once it
            // settles, after LOGIN_SUCCESS has already been dispatched below, so it never
            // gates rendering.
            const welcomeStart = performance.now();
            const welcomeStatusPromise = welcomeAPI.getStatus()
              .then((ws) => ({ seen: !!ws.seen, ms: performance.now() - welcomeStart }))
              .catch((welcomeErr) => {
                console.error('Welcome status check failed:', welcomeErr);
                return { seen: true, ms: performance.now() - welcomeStart }; // fail open
              });

            const pkgStart = performance.now();
            const notificationPackagePromise = paymentAPI.getNotificationPackage()
              .then((pkg) => ({ pkg, ms: performance.now() - pkgStart }))
              .catch((pkgErr) => {
                console.error('Notification package fetch failed:', pkgErr);
                return { pkg: null, ms: performance.now() - pkgStart }; // fail open
              });

            const { seen: welcomeSeen, ms: welcomeMs } = await welcomeStatusPromise;

            dispatch({ type: 'LOGIN_SUCCESS', payload: { user: data.user, sessionId: data.sessionId } });
            dispatch({ type: 'SET_WELCOME_STATUS', payload: welcomeSeen });

            // Load payment status
            const paymentKey = `italyVisaPaymentCompleted_${data.user.UserID}`;
            const paymentCompleted = localStorage.getItem(paymentKey) === 'true';
            dispatch({ type: 'SET_PAYMENT_COMPLETED', payload: paymentCompleted });

            // The notification-package fetch has been running since before we awaited welcome
            // status above (often already settled by now) — apply its result here, strictly
            // after LOGIN_SUCCESS was dispatched, so it can never gate rendering (see comment
            // above). Also where the single structured perf log for this flow fires, once both
            // concurrent phases are known.
            notificationPackagePromise.then(({ pkg, ms }) => {
              if (pkg) dispatch({ type: 'SET_NOTIFICATION_PACKAGE', payload: pkg });
              logPhaseTimings('AppContext.verifyUserSession', {
                verify: verifyMs,
                welcomeStatus: welcomeMs,
                notificationPackage: ms,
                total: performance.now() - perfStart,
              });
            });

            // Load selected visa application
            const visaKey = `selectedVisaApplication_${data.user.UserID}`;
            const selectedVisa = localStorage.getItem(visaKey);
            if (selectedVisa) {
              dispatch({ type: 'SET_SELECTED_VISA_APPLICATION', payload: JSON.parse(selectedVisa) });
            }
            
            // Load selected country. Only accept canonical slugs — older builds (and the
            // pre-fix ApplicationsPage fallback) could persist the DISPLAY name ('İspanya'),
            // which then leaked into every countrySlug API write on this device forever.
            // A poisoned value is dropped and we fall through to server-side recovery below.
            const countryKey = `selectedCountry_${data.user.UserID}`;
            const storedCountry = localStorage.getItem(countryKey);
            const selectedCountry = storedCountry && /^[a-z0-9_-]+$/.test(storedCountry)
              ? storedCountry
              : null;
            if (storedCountry && !selectedCountry) {
              localStorage.removeItem(countryKey);
            }
            if (selectedCountry) {
              dispatch({ type: 'SET_SELECTED_COUNTRY', payload: selectedCountry });
            } else {
              // Best-effort server-side recovery: a fresh browser / cleared storage / different
              // device otherwise leaves the whole chat surface half-dead (New Chat disabled,
              // starter chips fall back to generic phrasing, the widget's input disabled) even
              // though the user already has an application. Fail-open like the welcome/
              // notification-package checks above — never blocks rendering, and never guesses
              // a country when there's no single sensible candidate.
              try {
                const recoveredSlug = await fetchRecoverableCountrySlug();
                if (recoveredSlug) {
                  dispatch({ type: 'SET_SELECTED_COUNTRY', payload: recoveredSlug });
                }
              } catch (recoverErr) {
                console.error('Selected-country recovery failed:', recoverErr);
              }
            }

          } else {
            dispatch({ type: 'LOGOUT' });
          }
        } catch (error) {
          console.error('Session verification failed:', error);
          dispatch({ type: 'LOGOUT' });
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    verifyUserSession();
  }, []);

  // T16: fire chat-api's KB warmup (T15) once per (user, country) per browser session, right at
  // the single earliest reliable point a country is known — state.selectedCountry becoming set
  // or changing — regardless of which of the three call sites set it (MainPage, WelcomePage,
  // ApplicationsPage) or whether it was restored from localStorage / server-side recovery above.
  // Deliberately one choke point here instead of duplicating the trigger at each page. Gated by
  // a sessionStorage flag, same one-per-session convention ChatWidget.jsx uses for its welcome
  // bubble (WELCOME_BUBBLE_KEY) — never refires for the same (user, country) within a session,
  // including on re-renders/remounts. Never awaited and never allowed to throw here:
  // chatAPI.warmupKnowledgeBase already swallows/logs its own failures; the extra .catch is
  // defense-in-depth only, in case a caller-supplied stub bypasses that.
  useEffect(() => {
    const userId = state.currentUser?.UserID;
    const countrySlug = state.selectedCountry;
    if (!userId || !countrySlug) return;

    const warmedKey = `kb_warmed_${userId}_${countrySlug}`;
    try {
      if (sessionStorage.getItem(warmedKey) === '1') return;
      sessionStorage.setItem(warmedKey, '1');
    } catch {
      // sessionStorage unavailable (e.g. private browsing) — still attempt the warmup below;
      // worst case it fires more than once, which the backend's TTL'd cache already tolerates.
    }

    chatAPI.warmupKnowledgeBase(countrySlug).catch(() => { /* never surfaced — see comment above */ });
  }, [state.currentUser?.UserID, state.selectedCountry]);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {!state.isLoading && children}
    </AppContext.Provider>
  );
}

export function useAppContext() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
}
 